import type { Vec2 } from '../types.ts';
import { elevatedBodiesInPaintOrder } from './elevationOrder.ts';
import { resolveElevationStack } from './elevationStack.ts';
import { visualColumnBounds, type ColumnAabb } from './extrude.ts';
import { tessellateRing } from './ring.ts';
import type { MapDocument } from './types.ts';

export type ViewRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type CachedBounds = { elevation: number; base: number; box: ColumnAabb | null };

/**
 * Painted-column AABB per tessellated outline. `tessellateRing` hands back the same
 * array while a ring is unchanged, so the outline's identity is the cache key and the
 * per-frame cull never walks a body's points twice.
 */
const boundsByOutline = new WeakMap<readonly Vec2[], CachedBounds>();

export function cachedColumnBounds(
  outline: readonly Vec2[],
  elevation: number,
  base: number,
): ColumnAabb | null {
  const hit = boundsByOutline.get(outline);

  if (hit && hit.elevation === elevation && hit.base === base) {
    return hit.box;
  }

  const box = visualColumnBounds(outline, elevation, base);
  boundsByOutline.set(outline, { elevation, base, box });
  return box;
}

/** True when `next` sits within `slack` world units of `last` on every edge. */
export function viewsWithin(last: ViewRect, next: ViewRect, slack: number): boolean {
  return (
    Math.abs(next.x - last.x) <= slack &&
    Math.abs(next.y - last.y) <= slack &&
    Math.abs(next.width - last.width) <= slack &&
    Math.abs(next.height - last.height) <= slack
  );
}

function viewIsUsable(view: ViewRect): boolean {
  return (
    Number.isFinite(view.x) &&
    Number.isFinite(view.y) &&
    Number.isFinite(view.width) &&
    Number.isFinite(view.height) &&
    view.width > 0 &&
    view.height > 0
  );
}

function boxOverlapsView(
  box: { minX: number; minY: number; maxX: number; maxY: number },
  view: ViewRect,
): boolean {
  return (
    box.maxX >= view.x &&
    box.minX <= view.x + view.width &&
    box.maxY >= view.y &&
    box.minY <= view.y + view.height
  );
}

function allBodyIds(map: MapDocument): Set<string> {
  const ids = new Set<string>();

  for (const island of map.islands) {
    ids.add(island.id);
  }

  for (const rock of map.rocks) {
    ids.add(rock.id);
  }

  return ids;
}

/**
 * Body ids whose painted column (waterline plus screen-up plateau) overlaps
 * `view`. A degenerate camera never blanks the map — every body stays visible.
 * `elevate` false uses the waterline box only (editor with Elevation off).
 */
export function visibleElevatedBodyIds(
  map: MapDocument,
  view: ViewRect,
  elevate = true,
): Set<string> {
  if (!viewIsUsable(view)) {
    return allBodyIds(map);
  }

  const stack = resolveElevationStack(map);
  const visible = new Set<string>();

  for (const entry of elevatedBodiesInPaintOrder(map)) {
    const resolved = stack.get(entry.body.id);
    const world = resolved?.worldElevation ?? entry.body.elevation;
    const base = resolved?.baseElevation ?? 0;
    const box = cachedColumnBounds(
      tessellateRing(entry.body),
      elevate ? world : 0,
      elevate ? base : 0,
    );

    if (!box || boxOverlapsView(box, view)) {
      visible.add(entry.body.id);
    }
  }

  return visible;
}
